"use client";

import React, { memo, useMemo, ReactNode } from "react";
import { Index, Configure } from "react-instantsearch";
import { buildMeiliFilterString } from "@/core/search";
import { MindstringPrototype } from "@/generated/fastapi";

const EMPTY_ADDITIONAL_FILTERS: string[] = [];

interface MemoizedSearchIndexProps {
  indexName: string;
  indexId: string;
  prototype?: MindstringPrototype;
  situationId?: string;
  hitsPerPage: number;
  mainContentField: string;
  previewLength: number;
  sortBy?: string;
  languageFilters: string[];
  additionalFilters?: string[];
  children: ReactNode;
}

/**
 * Memoized wrapper around InstantSearch Index + Configure
 * Prevents rebuilding filter strings and config on every render
 */
function MemoizedSearchIndex({
  indexName,
  indexId,
  prototype,
  situationId,
  hitsPerPage,
  mainContentField,
  previewLength,
  sortBy,
  languageFilters,
  additionalFilters = EMPTY_ADDITIONAL_FILTERS,
  children,
}: MemoizedSearchIndexProps) {
  // Note: semanticRatio is configured at client level in core search runtime (not per-index)

  // Memoize filter string - recalculates when prototype, situationId, or languageFilters change
  const filters = useMemo(() => {
    const baseFilters = buildMeiliFilterString({
      ...(prototype && { prototype }),
      ...(situationId && { situationId }),
      ...(languageFilters &&
        languageFilters.length > 0 && { languageFilters }),
    });

    return [baseFilters, ...additionalFilters]
      .filter(Boolean)
      .join(" AND ");
  }, [prototype, situationId, languageFilters, additionalFilters]);

  // Memoize Configure props (without hybrid - that's handled by searchClient Proxy)
  const configureProps = useMemo(() => {
    const props = {
      filters,
      hitsPerPage,
      attributesToHighlight: [mainContentField],
      attributesToSnippet: [`${mainContentField}:${previewLength}`],
      snippetEllipsisText: "...",
      // Pass indexId so the searchClient Proxy can map it to semanticRatio
      indexId,
    };

    // // Debug log Configure props
    // if (
    //   typeof window !== "undefined" &&
    //   window.localStorage.getItem("meili_debug") === "1"
    // ) {
    // }

    return props;
  }, [filters, hitsPerPage, mainContentField, previewLength, indexId]);

  // // Debug log when Configure props change
  // useEffect(() => {
  //   if (
  //     typeof window !== "undefined" &&
  //     window.localStorage.getItem("meili_debug") === "1"
  //   ) {
  //       `[MemoizedSearchIndex ${indexId}] Configure props updated:`,
  //       configureProps,
  //     );
  //   }
  // }, [configureProps, indexId]);

  // Use sortBy as indexName if provided (MeiliSearch replica indexes for sorting)
  const effectiveIndexName = sortBy || indexName;

  return (
    <Index indexName={effectiveIndexName} indexId={indexId}>
      <Configure {...configureProps} />
      {children}
    </Index>
  );
}

// Memoize to prevent re-renders when parent updates
export default memo(MemoizedSearchIndex);
